import { XY } from "../engine/xy";
import type { Sprite } from "./sprites/sprite";

export class Camera {
	cellWidth: number;
	cellHeight: number;
	viewportWidth: number = 0;
	viewportHeight: number = 0;
	offsetX: number = 0;
	offsetY: number = 0;

	constructor(cellWidth: number, cellHeight: number) {
		this.cellWidth = cellWidth;
		this.cellHeight = cellHeight;
	}

	setCellSize(width: number, height: number): void {
		this.cellWidth = width;
		this.cellHeight = height;
	}

	setViewportSize(width: number, height: number): void {
		this.viewportWidth = width;
		this.viewportHeight = height;
	}

	centerOn(xy: XY): void {
		this.offsetX = Math.round(xy.x * this.cellWidth + this.cellWidth / 2 - this.viewportWidth / 2);
		this.offsetY = Math.round(xy.y * this.cellHeight + this.cellHeight / 2 - this.viewportHeight / 2);
	}

	centerOnPixel(worldPixelX: number, worldPixelY: number): void {
		this.offsetX = Math.round(worldPixelX - this.viewportWidth / 2);
		this.offsetY = Math.round(worldPixelY - this.viewportHeight / 2);
	}

	centerOnBoard(boardWidth: number, boardHeight: number): void {
		const boardPixelWidth = boardWidth * this.cellWidth;
		const boardPixelHeight = boardHeight * this.cellHeight;
		this.offsetX = Math.round((boardPixelWidth - this.viewportWidth) / 2);
		this.offsetY = Math.round((boardPixelHeight - this.viewportHeight) / 2);
	}

	getVisibleCellRange(
		boardWidth: number,
		boardHeight: number,
	): {
		minX: number;
		minY: number;
		maxX: number;
		maxY: number;
	} {
		const minX = Math.max(0, Math.floor(this.offsetX / this.cellWidth) - 1);
		const maxX = Math.min(boardWidth - 1, Math.floor((this.offsetX + this.viewportWidth) / this.cellWidth) + 1);
		const minY = Math.max(0, Math.floor(this.offsetY / this.cellHeight) - 1);
		const maxY = Math.min(boardHeight - 1, Math.floor((this.offsetY + this.viewportHeight) / this.cellHeight) + 1);
		return { minX, minY, maxX, maxY };
	}

	pixelToBoard(pixelX: number, pixelY: number): XY {
		const boardX = Math.floor((pixelX + this.offsetX) / this.cellWidth);
		const boardY = Math.floor((pixelY + this.offsetY) / this.cellHeight);
		return new XY(boardX, boardY);
	}

	getCellCenterPixel(xy: XY): { x: number; y: number } {
		return {
			x: xy.x * this.cellWidth + this.cellWidth / 2 - this.offsetX,
			y: xy.y * this.cellHeight + this.cellHeight / 2 - this.offsetY,
		};
	}

	boardToPixel(xy: XY): { x: number; y: number } {
		return {
			x: xy.x * this.cellWidth - this.offsetX,
			y: xy.y * this.cellHeight - this.offsetY,
		};
	}

	boardToPixelWithSprite(xy: XY, sprite: Sprite): { x: number; y: number } {
		let { x, y } = this.boardToPixel(xy);
		x += sprite.offsetX * this.cellWidth;
		y -= (sprite.height - 1) * this.cellHeight;
		y -= sprite.offsetY * this.cellHeight;
		return { x, y };
	}

	isOnScreen(pixelX: number, pixelY: number, pixelWidth: number, pixelHeight: number): boolean {
		return (
			pixelX + pixelWidth > 0 && pixelX < this.viewportWidth && pixelY + pixelHeight > 0 && pixelY < this.viewportHeight
		);
	}
}
